<!--
/**
 * FormKitContactSelect
 *
 * Custom FormKit input that wraps PrimeVue AutoComplete for contact selection.
 * Fetches options via useBffClient().getContacts() with name/email search.
 * Supports dual mode: inside a FormKit form (context) or standalone with v-model.
 *
 * @description Contact autocomplete with server-side search and bounded in-memory cache.
 *
 * FormKit usage (e.g. in a form):
 *   <FormKit type="contactSelect" name="contactId" label="Contact"
 *            placeholder="Search contacts..." v-model="form.contactId" />
 *
 * Standalone usage (e.g. in a card/record view):
 *   <FormKitContactSelect v-model="selectedContactId" placeholder="Search contacts..."
 *                          :disabled="false" />
 *
 * @prop {Object} [context] - FormKit framework context (provided when used as FormKit input).
 *   Context may include: placeholder, pageSize, minSearchLength, disabled.
 * @prop {string} [modelValue=''] - Selected contact id (string).
 * @prop {string} [placeholder=''] - Placeholder text when nothing selected. Empty = non-breaking space.
 * @prop {boolean} [disabled=false] - Disable the input.
 * @emits {string} update:modelValue - Emitted with the selected contact id when selection changes.
 */
-->
<template>
  <div v-bind="attrs" :aria-required="isRequired" :class="wrapperClass">
    <AutoComplete
      :input-id="inputId"
      :model-value="displayValue"
      :suggestions="suggestions"
      option-label="label"
      option-value="id"
      :placeholder="displayPlaceholder"
      :disabled="disabled"
      :show-clear="true"
      complete-on-focus
      class="w-full"
      data-testid="formkit-contact-select-input"
      @complete="onSearch"
      @update:model-value="onSelect"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed } from 'vue'
import type { FormKitFrameworkContext } from '@formkit/core'
import type { Contact } from '~/models/graphql/generated/types'
import {
  ContactFilterFieldEnum,
  StringOperatorEnum,
} from '~/models/graphql/generated/types'
import {
  useFormKitAutoComplete,
  type AutoCompleteOption,
} from '~/composables/useFormKitAutoComplete'

export type ContactOption = AutoCompleteOption

const PAGE_SIZE = 25
const MIN_SEARCH_LENGTH = 2

const props = defineProps({
  context: {
    type: Object as PropType<
      FormKitFrameworkContext & {
        placeholder?: string
        pageSize?: number
        minSearchLength?: number
        disabled?: boolean
      }
    >,
    default: undefined,
  },
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const bffClient = useBffClient()

const pageSize = computed(() => props.context?.pageSize ?? PAGE_SIZE)
const minSearchLength = computed(
  () => props.context?.minSearchLength ?? MIN_SEARCH_LENGTH
)

function optionFromContact(contact: Contact): ContactOption {
  const name =
    typeof contact.name === 'string' && contact.name.trim().length > 0
      ? contact.name.trim()
      : ''
  const email =
    typeof contact.email === 'string' && contact.email.trim().length > 0
      ? contact.email.trim()
      : undefined

  let label = name || email || '(No name)'
  if (name && email) label = `${name} (${email})`

  return { id: contact.id as string, label }
}

async function search(query: string): Promise<ContactOption[]> {
  const trimmed = query?.trim()
  if (!trimmed || trimmed.length < minSearchLength.value) return []

  try {
    const byName = await bffClient.getContacts(1, pageSize.value, [
      {
        field: ContactFilterFieldEnum.Name,
        operator: StringOperatorEnum.Contains,
        value: trimmed,
      },
    ])

    let contacts = (byName?.collection ?? []) as Contact[]

    if (!contacts.length) {
      const byEmail = await bffClient.getContacts(1, pageSize.value, [
        {
          field: ContactFilterFieldEnum.Email,
          operator: StringOperatorEnum.Contains,
          value: trimmed,
        },
      ])
      contacts = (byEmail?.collection ?? []) as Contact[]
    }

    return contacts.map(optionFromContact)
  } catch {
    return []
  }
}

async function loadById(id: string): Promise<ContactOption | null> {
  try {
    const contact = await bffClient.getContact(id)
    if (contact) return optionFromContact(contact)
  } catch {
    // Leave display unchanged so existing label can remain
  }
  return null
}

const {
  suggestions,
  displayValue,
  displayPlaceholder,
  disabled,
  inputId,
  isRequired,
  attrs,
  onSearch,
  onSelect,
  wrapperClass,
} = useFormKitAutoComplete({ props, emit, search, loadById })
</script>
